import type { DriverCaptureEntryView } from '@chukua/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { listDriverCapture, recordDriverStep, uploadDriverPhoto } from '../dispatch.api';
import { buildCaptureEvent, CAPTURE_STEP_DESCRIPTIONS, type CaptureFormValues } from '../policies/capture-step.policy';

export type DriverCaptureState = { status: 'loading' } | { status: 'loaded'; entries: DriverCaptureEntryView[] } | { status: 'error' };

/**
 * Recording a driver's step from the office (gitaumoses4/chukua#79), and the list of what the trip has. An attempt keeps its key, and the ids of
 * photos already confirmed, until a step is saved: a refusal or a dropped photo then retries the same step rather than starting a second one.
 */
export function useDriverCapture(tripId: string, driverName: string) {
  const toast = useToast();
  const [state, setState] = useState<DriverCaptureState>({ status: 'loading' });
  const [readCount, setReadCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attempt = useRef({ key: crypto.randomUUID(), confirmed: new Map<File, string>() });

  useEffect(() => {
    let cancelled = false;
    listDriverCapture(tripId)
      .then((entries) => !cancelled && setState({ status: 'loaded', entries }))
      .catch(() => !cancelled && setState({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [tripId, readCount]);

  const record = useCallback(
    async (values: CaptureFormValues, photos: File[]): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);
      try {
        for (const photo of photos) {
          if (attempt.current.confirmed.has(photo)) continue;
          const clientMediaId = crypto.randomUUID();
          await uploadDriverPhoto(tripId, photo, clientMediaId);
          attempt.current.confirmed.set(photo, clientMediaId);
        }
        const mediaIds = photos.flatMap((photo) => attempt.current.confirmed.get(photo) ?? []);
        await recordDriverStep(tripId, buildCaptureEvent(values, { key: attempt.current.key, mediaIds }));
        toast.success(`${CAPTURE_STEP_DESCRIPTIONS[values.step].noun} recorded for ${driverName}`);
        attempt.current = { key: crypto.randomUUID(), confirmed: new Map() };
        setReadCount((count) => count + 1);
        return true;
      } catch (err) {
        setError(apiErrorMessage(err) ?? 'Could not record this step. Check the connection and try again.');
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [tripId, driverName, toast],
  );

  return { state, record, isSubmitting, error };
}
